import { WizardSteps } from '../../initial-state-builders/index';

import type { InitialWizardState } from '../../../types';

export type ValidationResult = {
  errors: string[];
  valid: boolean;
};

/**
 * Validates initial wizard state structure to catch errors early.
 *
 * @param state - The initial state to validate
 * @returns Validation result with any errors found
 *
 * @example
 * const result = validateInitialState(state);
 * if (!result.valid) {
 *   console.error('Invalid initial state:', result.errors);
 * }
 */
export function validateInitialState(state: InitialWizardState): ValidationResult {
  const errors: string[] = [];

  // Validate step index
  if (state.defaultStepToOpenOn !== undefined) {
    const maxStepIndex = Math.max(...Object.values(WizardSteps));
    const minStepIndex = Math.min(...Object.values(WizardSteps));

    if (state.defaultStepToOpenOn < minStepIndex || state.defaultStepToOpenOn > maxStepIndex) {
      errors.push(
        `Invalid step index: ${String(state.defaultStepToOpenOn)}. Must be between ${minStepIndex} and ${maxStepIndex}.`
      );
    }
  }

  // Validate requirements step structure
  if (state.requirementsStep) {
    Object.keys(state.requirementsStep).forEach((key) => {
      // Skip meta properties
      if (key === 'visited' || key === 'activeFilter') {
        return;
      }

      const index = Number(key);
      if (Number.isNaN(index)) {
        errors.push(`Invalid requirement key: "${key}". Must be a number, 'visited', or 'activeFilter'.`);
      } else if (index < 1) {
        errors.push(`Invalid requirement index: ${index}. Must be >= 1.`);
      }
    });
  }

  // Validate date ranges if provided
  if (state.detailsStep?.startDate && state.detailsStep?.endDate) {
    const { startDate } = state.detailsStep;
    const { endDate } = state.detailsStep;

    if (startDate > endDate) {
      errors.push('Start date must be before end date.');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
